import * as plugins from './plugins.js';
import {
  VirtualStream,
  VirtualStreamActivationScope,
} from './classes.virtualstream.js';
import { TypedResponseError } from './classes.typedresponseerror.js';
import { dispatchTypedRequestHook } from './internal.hooks.js';
import { TypedTarget } from './classes.typedtarget.js';

const webrequestInstance = new plugins.webrequest.WebrequestClient();
const maxTimeoutMs = 2_147_483_647;
const virtualStreamFrameTimeoutMs = 10_000;

export interface ITypedRequestFireOptions {
  useCache?: boolean;
  timeoutMs?: number;
  maxRetries?: number;
}

interface IResolvedTypedRequestFireOptions {
  useCache: boolean;
  timeoutMs?: number;
  maxRetries: number;
  deadlineAt?: number;
  cacheKey?: string;
}

export class TypedRequestTimeoutError extends Error {
  public readonly method: string;
  public readonly timeoutMs: number;

  constructor(methodArg: string, timeoutMsArg: number) {
    super(`Typed request "${methodArg}" timed out after ${timeoutMsArg}ms`);
    this.name = 'TypedRequestTimeoutError';
    this.method = methodArg;
    this.timeoutMs = timeoutMsArg;
  }
}

export class TypedRequestRetryLimitError extends Error {
  public readonly method: string;
  public readonly maxRetries: number;

  constructor(methodArg: string, maxRetriesArg: number) {
    super(
      `Typed request "${methodArg}" exceeded its ${maxRetriesArg} server-directed retries`
    );
    this.name = 'TypedRequestRetryLimitError';
    this.method = methodArg;
    this.maxRetries = maxRetriesArg;
  }
}

export class TypedRequest<T extends plugins.typedRequestInterfaces.ITypedRequest> {
  /**
   * in case we post against a url endpoint
   */
  public urlEndPoint?: string;

  /**
   * in case we post against a TypedTarget
   */
  typedTarget!: TypedTarget;

  public method: string;

  /**
   * When true, hooks will not be called for this request.
   * Use this for internal/logging requests to prevent infinite loops.
   */
  public skipHooks: boolean = false;

  /**
   * @param postEndPointArg
   * @param methodArg
   */
  constructor(postTarget: string | TypedTarget, methodArg: T['method']) {
    if (typeof postTarget === 'string') {
      this.urlEndPoint = postTarget;
    } else {
      this.typedTarget = postTarget;
    }
    this.method = methodArg;
  }

  /**
   * fires the request
   */
  public async fire(
    fireArg: T['request'],
    fireOptionsArg: boolean | ITypedRequestFireOptions = false
  ): Promise<T['response']> {
    const requestStartTime = Date.now();
    const fireOptions = this.resolveFireOptions(fireOptionsArg);
    const activationScope = new VirtualStreamActivationScope();

    let payloadSending: plugins.typedRequestInterfaces.ITypedRequest = {
      method: this.method,
      request: fireArg,
      response: null as any,
      correlation: {
        id: plugins.isounique.uni(),
        phase: 'request',
      },
    };

    try {
      payloadSending = VirtualStream.encodePayloadForNetwork(payloadSending, {
        sendMethod: (
          payloadArg: plugins.typedRequestInterfaces.IStreamRequest
        ) =>
          this.postStreamTrObject(payloadArg),
        activationScope,
      });

      const payloadContainsVirtualStream =
        VirtualStream.containsNetworkDescriptor(payloadSending);
      if (
        payloadContainsVirtualStream &&
        !this.urlEndPoint &&
        this.typedTarget.options.supportsAbortSignal !== true
      ) {
        throw new Error(
          'VirtualStreams require TypedTarget AbortSignal transport support'
        );
      }
      if (
        fireOptions.useCache &&
        payloadContainsVirtualStream
      ) {
        throw new Error(
          'TypedRequest response caching is not supported for requests containing VirtualStreams'
        );
      }
      this.assertDeadline(fireOptions);
      if (fireOptions.useCache) {
        fireOptions.cacheKey = await this.createCacheKey(payloadSending);
        this.assertDeadline(fireOptions);
      }

      if (!this.skipHooks) {
        dispatchTypedRequestHook('onOutgoingRequest', {
          correlationId: payloadSending.correlation!.id,
          method: this.method,
          direction: 'outgoing',
          phase: 'request',
          timestamp: requestStartTime,
          payload: fireArg,
        });
      }

      let payloadReceiving = await this.postTrObject(
        payloadSending,
        fireOptions
      );
      const finalResponseError = payloadReceiving.error;
      this.assertDeadline(fireOptions);

      if (finalResponseError) {
        if (!this.skipHooks) {
          dispatchTypedRequestHook('onIncomingResponse', {
            correlationId: payloadSending.correlation!.id,
            method: this.method,
            direction: 'incoming',
            phase: 'response',
            timestamp: Date.now(),
            durationMs: Date.now() - requestStartTime,
            payload: payloadReceiving.response,
            errorText: finalResponseError.text,
          });
        }
        this.assertDeadline(fireOptions);
        throw new TypedResponseError(
          finalResponseError.text,
          finalResponseError.data
        );
      }

      const responseContainsVirtualStream =
        VirtualStream.containsNetworkDescriptor(payloadReceiving);
      if (
        responseContainsVirtualStream &&
        !this.urlEndPoint &&
        this.typedTarget.options.supportsAbortSignal !== true
      ) {
        throw new Error(
          'VirtualStreams require TypedTarget AbortSignal transport support'
        );
      }
      if (
        fireOptions.useCache &&
        fireOptions.cacheKey &&
        responseContainsVirtualStream
      ) {
        await webrequestInstance.deleteCache(fireOptions.cacheKey);
        this.assertDeadline(fireOptions);
      }

      payloadReceiving = VirtualStream.decodePayloadFromNetwork(
        payloadReceiving,
        {
          sendMethod: (
            payloadArg: plugins.typedRequestInterfaces.IStreamRequest
          ) =>
            this.postStreamTrObject(payloadArg),
          activationScope,
        }
      );
      this.assertDeadline(fireOptions);

      if (!this.skipHooks) {
        dispatchTypedRequestHook('onIncomingResponse', {
          correlationId: payloadSending.correlation!.id,
          method: this.method,
          direction: 'incoming',
          phase: 'response',
          timestamp: Date.now(),
          durationMs: Date.now() - requestStartTime,
          payload: payloadReceiving?.response,
        });
      }
      this.assertDeadline(fireOptions);

      activationScope.commit();
      return payloadReceiving.response;
    } catch (error) {
      await activationScope.rollback(error);
      throw error;
    }
  }

  private async postTrObject(
    payloadSendingArg: plugins.typedRequestInterfaces.ITypedRequest,
    fireOptionsArg: IResolvedTypedRequestFireOptions = {
      useCache: false,
      maxRetries: 3,
    },
  ): Promise<plugins.typedRequestInterfaces.ITypedRequest> {
    let retryCount = 0;
    while (true) {
      this.assertDeadline(fireOptionsArg);
      let payloadReceiving: plugins.typedRequestInterfaces.ITypedRequest;
      if (this.urlEndPoint) {
        try {
          const response = await webrequestInstance.postJson(
            this.urlEndPoint,
            payloadSendingArg,
            {
              cacheKey: fireOptionsArg.cacheKey,
              cacheStrategy: fireOptionsArg.useCache
                ? 'cache-first'
                : 'network-only',
              timeout: this.getRemainingTimeout(fireOptionsArg),
            }
          );
          payloadReceiving = response;
        } catch (error) {
          if (
            fireOptionsArg.timeoutMs !== undefined &&
            (this.hasDeadlineExpired(fireOptionsArg) ||
              plugins.webrequest.isWebrequestTimeoutError(error))
          ) {
            throw this.createTimeoutError(fireOptionsArg);
          }
          throw error;
        }
      } else {
        payloadReceiving = await this.postTypedTarget(
          payloadSendingArg,
          fireOptionsArg
        );
      }
      this.assertDeadline(fireOptionsArg);
      if (payloadReceiving.error) {
        if (!payloadReceiving.retry) {
          return payloadReceiving;
        }
      }
      if (!payloadReceiving.retry) {
        return payloadReceiving;
      }
      console.log(`Server requested a retry for TypedRequest method '${this.method}'`);
      if (retryCount >= fireOptionsArg.maxRetries) {
        throw new TypedRequestRetryLimitError(
          this.method,
          fireOptionsArg.maxRetries
        );
      }
      retryCount += 1;
      await this.waitForRetry(payloadReceiving.retry.waitForMs, fireOptionsArg);
    }
  }

  private async postStreamTrObject(
    payloadSendingArg: plugins.typedRequestInterfaces.IStreamRequest
  ): Promise<plugins.typedRequestInterfaces.IStreamRequest> {
    const streamFrameOptions: IResolvedTypedRequestFireOptions = {
      useCache: false,
      maxRetries: 3,
      timeoutMs: virtualStreamFrameTimeoutMs,
      deadlineAt:
        performance.now() + virtualStreamFrameTimeoutMs,
    };
    return this.postTrObject(
      payloadSendingArg,
      streamFrameOptions
    ) as Promise<plugins.typedRequestInterfaces.IStreamRequest>;
  }

  private async postTypedTarget(
    payloadSendingArg: plugins.typedRequestInterfaces.ITypedRequest,
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): Promise<plugins.typedRequestInterfaces.ITypedRequest> {
    const remainingTimeout = this.getRemainingTimeout(fireOptionsArg);
    if (remainingTimeout === undefined) {
      return this.typedTarget.post(payloadSendingArg);
    }
    const abortController = new AbortController();
    const timeout = setTimeout(() => {
      abortController.abort(this.createTimeoutError(fireOptionsArg));
    }, remainingTimeout);
    timeout.unref?.();
    try {
      return await this.typedTarget.post(payloadSendingArg, {
        signal: abortController.signal,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        throw this.createTimeoutError(fireOptionsArg);
      }
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  private resolveFireOptions(
    fireOptionsArg: boolean | ITypedRequestFireOptions
  ): IResolvedTypedRequestFireOptions {
    const fireOptions =
      typeof fireOptionsArg === 'boolean'
        ? { useCache: fireOptionsArg }
        : fireOptionsArg;

    if (!fireOptions || typeof fireOptions !== 'object') {
      throw new TypeError('TypedRequest fire options must be a boolean or an options object');
    }
    if (
      fireOptions.useCache !== undefined &&
      typeof fireOptions.useCache !== 'boolean'
    ) {
      throw new TypeError('TypedRequest useCache must be a boolean');
    }
    if (
      fireOptions.timeoutMs !== undefined &&
      (!Number.isSafeInteger(fireOptions.timeoutMs) ||
        fireOptions.timeoutMs <= 0 ||
        fireOptions.timeoutMs > maxTimeoutMs)
    ) {
      throw new RangeError(
        `TypedRequest timeoutMs must be a positive safe integer no greater than ${maxTimeoutMs}`
      );
    }
    if (
      fireOptions.maxRetries !== undefined &&
      (!Number.isSafeInteger(fireOptions.maxRetries) ||
        fireOptions.maxRetries < 0 ||
        fireOptions.maxRetries > 1_000)
    ) {
      throw new RangeError(
        'TypedRequest maxRetries must be a safe integer between 0 and 1000'
      );
    }
    if (
      fireOptions.timeoutMs !== undefined &&
      !this.urlEndPoint &&
      this.typedTarget.options.supportsAbortSignal !== true
    ) {
      throw new Error(
        'TypedRequest timeoutMs requires an HTTP URL or a TypedTarget with AbortSignal support'
      );
    }

    return {
      useCache: fireOptions.useCache ?? false,
      timeoutMs: fireOptions.timeoutMs,
      maxRetries: fireOptions.maxRetries ?? 3,
      deadlineAt:
        fireOptions.timeoutMs === undefined
          ? undefined
          : performance.now() + fireOptions.timeoutMs,
    };
  }

  private getRemainingTimeout(
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): number | undefined {
    if (
      fireOptionsArg.deadlineAt === undefined ||
      fireOptionsArg.timeoutMs === undefined
    ) {
      return undefined;
    }
    const remainingMs = Math.ceil(
      fireOptionsArg.deadlineAt - performance.now()
    );
    if (remainingMs <= 0) {
      throw this.createTimeoutError(fireOptionsArg);
    }
    return remainingMs;
  }

  private assertDeadline(
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): void {
    this.getRemainingTimeout(fireOptionsArg);
  }

  private hasDeadlineExpired(
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): boolean {
    return (
      fireOptionsArg.deadlineAt !== undefined &&
      performance.now() >= fireOptionsArg.deadlineAt
    );
  }

  private createTimeoutError(
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): TypedRequestTimeoutError {
    return new TypedRequestTimeoutError(
      this.method,
      fireOptionsArg.timeoutMs!
    );
  }

  private async waitForRetry(
    waitForMsArg: number,
    fireOptionsArg: IResolvedTypedRequestFireOptions
  ): Promise<void> {
    if (
      !Number.isSafeInteger(waitForMsArg) ||
      waitForMsArg <= 0 ||
      waitForMsArg > maxTimeoutMs
    ) {
      throw new RangeError(
        `TypedRequest retry waitForMs must be a positive safe integer no greater than ${maxTimeoutMs}`
      );
    }
    const remainingMs = this.getRemainingTimeout(fireOptionsArg);
    if (remainingMs === undefined) {
      await plugins.smartdelay.delayFor(waitForMsArg);
      return;
    }
    if (waitForMsArg >= remainingMs) {
      await plugins.smartdelay.delayFor(remainingMs);
      throw this.createTimeoutError(fireOptionsArg);
    }
    await plugins.smartdelay.delayFor(waitForMsArg);
    this.assertDeadline(fireOptionsArg);
  }

  private async createCacheKey(
    payloadSendingArg: plugins.typedRequestInterfaces.ITypedRequest
  ): Promise<string> {
    const cacheIdentity = JSON.stringify({
      endpoint: this.urlEndPoint,
      method: payloadSendingArg.method,
      request: payloadSendingArg.request,
    });
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(cacheIdentity)
    );
    const digestHex = Array.from(new Uint8Array(digest), (byteArg) =>
      byteArg.toString(16).padStart(2, '0')
    ).join('');
    return `typedrequest:v1:${digestHex}`;
  }
}
